<script setup>
import { ref, computed } from 'vue';

// Per-conversation transport picker for a WhatsApp Cloud inbox that has a paired Z-API channel.
// Lets the agent send THIS conversation's replies via the Official API or via Z-API (e.g. to
// reach a contact outside the 24h window). Purely a UI hint — the actual routing happens server
// side from content_attributes.transport.
const props = defineProps({
  modelValue: { type: String, default: 'cloud' }, // 'cloud' | 'zapi'
  // True when the conversation is past the 24h window: the Official API then needs a template,
  // so Z-API is the practical choice. We only NUDGE — never auto-switch.
  outsideWindow: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const open = ref(false);

// UI copy lives here (bound via {{ }}) so no bare template strings — the product is pt-BR.
const COPY = {
  sendBy: 'Enviar por:',
  dropdownTitle: 'Enviar esta conversa por:',
  outsideHint: 'Fora das 24h — considere API-ALTERNATIVA',
};

const OPTIONS = {
  cloud: {
    label: 'API Oficial',
    hint: 'WhatsApp Business · dentro das 24h ou com template',
    icon: 'i-lucide-shield-check',
    pill: 'bg-n-blue-3 text-n-blue-11',
    dot: 'text-n-blue-11',
  },
  zapi: {
    label: 'API-ALTERNATIVA',
    hint: 'Envia a qualquer hora, sem template',
    icon: 'i-lucide-zap',
    pill: 'bg-n-amber-3 text-n-amber-11',
    dot: 'text-n-amber-11',
  },
  auto: {
    label: 'Automático',
    hint: 'Alterna a cada mensagem — economiza a API Oficial',
    icon: 'i-lucide-shuffle',
    pill: 'bg-n-iris-3 text-n-iris-11',
    dot: 'text-n-iris-11',
  },
};

const current = computed(() => OPTIONS[props.modelValue] || OPTIONS.cloud);
const suggestZapi = computed(
  () => props.outsideWindow && props.modelValue === 'cloud'
);

const select = value => {
  emit('update:modelValue', value);
  open.value = false;
};
</script>

<template>
  <div class="relative flex items-center gap-2 text-xs">
    <span class="text-n-slate-10">{{ COPY.sendBy }}</span>

    <button
      type="button"
      class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium transition-colors"
      :class="current.pill"
      @click="open = !open"
    >
      <span :class="current.icon" />
      {{ current.label }}
      <span class="i-lucide-chevron-down text-[10px]" />
    </button>

    <span v-if="suggestZapi" class="flex items-center gap-1 text-n-amber-11">
      <span class="i-lucide-triangle-alert text-[11px]" />
      {{ COPY.outsideHint }}
    </span>

    <!-- backdrop: closes on outside click without extra deps -->
    <div v-if="open" class="fixed inset-0 z-10" @click="open = false" />

    <div
      v-if="open"
      class="absolute bottom-full left-0 z-20 mb-1.5 w-72 rounded-xl bg-n-solid-1 p-1.5 shadow-lg outline outline-1 outline-n-container"
    >
      <p class="px-2 py-1 text-xs text-n-slate-10">{{ COPY.dropdownTitle }}</p>
      <button
        v-for="(opt, key) in OPTIONS"
        :key="key"
        type="button"
        class="flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-n-alpha-2"
        @click="select(key)"
      >
        <span :class="[opt.icon, opt.dot]" class="mt-0.5" />
        <span class="min-w-0 flex-1">
          <span
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ opt.label }}
            <span
              v-if="modelValue === key"
              class="i-lucide-check text-n-teal-11"
            />
          </span>
          <span class="block text-xs text-n-slate-11">{{ opt.hint }}</span>
        </span>
      </button>
    </div>
  </div>
</template>
